import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    const updatedVariant = event.detail.resource;
    
    if (!source) return;
    
    const newMediaGallery = source.querySelector('media-gallery');
    newMediaGallery.querySelectorAll("slideshow-slide.product-media-container").forEach((node) => {
      node.removeAttribute("hidden");
    });
    let filteredOutNodes = newMediaGallery.querySelectorAll(`slideshow-slide.product-media-container:not(:has(img[alt="${updatedVariant.option1}"]))`);

    // Check if any images actually have the matching alt attribute
    let matchingNodes = newMediaGallery.querySelectorAll(`slideshow-slide.product-media-container:has(img[alt="${updatedVariant.option1}"])`);
    
    // Only hide non-matching images if there are actually matching images
    if (matchingNodes.length > 0) {
      filteredOutNodes.forEach((node) => {
        node.setAttribute("hidden", "");
      });
    }
    // If no matching images found, leave all images visible (don't hide any)

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);

    const newSlideshow = newMediaGallery.querySelector('slideshow-component');
    
    if (newSlideshow && newSlideshow.refresh) {
      queueMicrotask(() => {
        newSlideshow.refresh();
      });
    }
    document.querySelector("media-gallery slideshow-controls").style.display = "none";
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
